import React from "react";
import { Box, Typography, Link } from "@mui/material";
import { ContentCopy, OpenInNew } from "@mui/icons-material";
import GoogleOtherCalendars from "src/assets/screenshots/google-other-calendars.png";
import GooglePasteUrl from "src/assets/screenshots/google-paste-url.png";
import GoogleCalendarUpsell from "./GoogleCalendarUpsell.js";
import { customEvent } from "src/utils/gtag.js";

// Reusable Android sync instructions component
const AndroidSyncInstructions = () => (
  <>
    You'll need to manually turn on sync for the calendar. In your Calendar app,
    tap <strong>Menu</strong> (☰) at the top left → <strong>Settings</strong> →
    scroll down and tap <strong>Show More</strong>. Find your new calendar and
    tap it → toggle the <strong>sync button</strong> at the top of the screen.
  </>
);

const GoogleFAQs = ({ onCopyLink }) => {
  const faqs = [
    {
      question: "I wasn't prompted to add the calendar.",
      answer: (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Step 1: </strong>
              <Link
                onClick={onCopyLink}
                sx={{ cursor: "pointer", textDecoration: "underline" }}
              >
                Copy this link
                <ContentCopy sx={{ fontSize: 14, ml: 0.5 }} />
              </Link>
            </Typography>

            <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
              <strong>Step 2: </strong>
              <Link
                onClick={() =>
                  window.open(
                    "https://calendar.google.com/calendar/u/0/r/settings/addbyurl",
                    "_blank"
                  )
                }
                sx={{ cursor: "pointer", textDecoration: "underline" }}
              >
                Open Google Calendar
                <OpenInNew sx={{ fontSize: 14, ml: 0.5 }} />
              </Link>{" "}
              and paste the link in the <strong>From URL</strong> box. Click on{" "}
              <strong>Add Calendar</strong> and you're good to go.
              <img
                src={GooglePasteUrl}
                alt="Google Calendar From URL dialog"
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  marginBottom: "16px",
                  borderRadius: "8px",
                }}
              />
            </Typography>

            <Typography variant="body1" sx={{ mt: 3, lineHeight: 1.5 }}>
              <strong>Step 3 (Android only): </strong>
              <AndroidSyncInstructions />
            </Typography>

            <Typography variant="body1" sx={{ mt: 4, lineHeight: 1.5 }}>
              <strong>Step 4: </strong>
              <Link
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                sx={{ cursor: "pointer", textDecoration: "underline" }}
              >
                Confirm that everything is working
              </Link>{" "}
              as expected or{" "}
              <Link
                onClick={() =>
                  window.open(
                    `https://docs.google.com/forms/d/e/${process.env.REACT_APP_SUPPORT_FORM_ID}/viewform`,
                    "_blank"
                  )
                }
                sx={{ cursor: "pointer", textDecoration: "underline" }}
              >
                submit a ticket
                <OpenInNew sx={{ fontSize: 14, ml: 0.5 }} />
              </Link>{" "}
              if you continue to have issues.
            </Typography>

            <Typography variant="body1" sx={{ mt: 4, lineHeight: 1.5 }}>
              <strong>Note: </strong>
              If you are not immediately taken to the page to paste in the URL,
              go to your{" "}
              <Link
                onClick={() =>
                  window.open(
                    "https://calendar.google.com/calendar/u/0/r/settings/addbyurl",
                    "_blank"
                  )
                }
                sx={{ cursor: "pointer", textDecoration: "underline" }}
              >
                Google Calendar
              </Link>{" "}
              and find the <strong>Other Calendars</strong> section on the
              bottom left.
              <br />
              <br />
              Select <strong>+</strong> and then <strong>From URL</strong>.
              <br />
              <br />
              <img
                src={GoogleOtherCalendars}
                alt="Google Calendar From URL dialog"
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  marginBottom: "16px",
                  borderRadius: "8px",
                }}
              />
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      question:
        'I see the calendar in my list of calendars but it shows up as "webcal://..."',
      answer: (
        <>
          This is a known issue with Google Calendar taking up to 24 hours to
          pull events and data for subscriptions (
          <Link
            href="https://support.google.com/calendar/answer/37100?hl=en#zippy=%2Cadd-a-calendar-by-url"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ cursor: "pointer", textDecoration: "underline" }}
          >
            see official Google Calendar post
            <OpenInNew sx={{ fontSize: 14, ml: 0.5 }} />
          </Link>
          ).
          <br />
          <br />
          <GoogleCalendarUpsell />
        </>
      ),
    },
    {
      question: "Events aren't showing up in my Google Calendar.",
      answer: (
        <>
          Make sure the calendar is toggled on in your list of calendars. If
          events still aren't appearing, there is a delay in pulling events.
          <br />
          <br />
          This is a known issue with Google Calendar taking up to 24 hours to
          pull events and data for subscriptions (
          <Link
            href="https://support.google.com/calendar/answer/37100?hl=en#zippy=%2Cadd-a-calendar-by-url"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ cursor: "pointer", textDecoration: "underline" }}
          >
            see official Google Calendar post
            <OpenInNew sx={{ fontSize: 14, ml: 0.5 }} />
          </Link>
          ).
          <br />
          <br />
          <GoogleCalendarUpsell />
        </>
      ),
    },
    {
      question: "The calendar isn't appearing on my Android phone calendar.",
      answer: (
        <>
          <AndroidSyncInstructions />
          <br />
          <br />
          <GoogleCalendarUpsell />
        </>
      ),
    },
    {
      question: "The calendar isn't appearing on my iPhone Google Calendar app.",
      answer: (
        <>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Step 1: </strong>Visit{" "}
            <Link
              onClick={() => window.open("https://calendar.google.com/calendar/syncselect", "_blank")}
              sx={{ cursor: "pointer", textDecoration: "underline" }}
            >
              this link
              <OpenInNew sx={{ fontSize: 14, ml: 0.5 }} />
            </Link>{" "}
            on your iPhone.
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Step 2: </strong>Sign in to your Google account.
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Step 3: </strong>Select all calendars you want to sync with your iPhone.
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Step 4: </strong>Tap Save.
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Step 5: </strong>Then go back to the iPhone Calendar app, pull down to refresh.
          </Typography>
          
          <GoogleCalendarUpsell />
        </>
      ),
    },
  ];

  return faqs.map((faq, index) => {
    const shortNames = [
      "not_prompted",
      "webcal_issue",
      "events_not_showing",
      "android_sync_issue",
      "iphone_sync_issue",
    ];
    const shortName = shortNames[index];

    return {
      ...faq,
      question: (
        <Box
          onClick={() =>
            customEvent({
              name: `faq_click_${index + 1}_${shortName}`,
              category: "FAQ",
              label: faq.question,
            })
          }
        >
          {faq.question}
        </Box>
      ),
    };
  });
};

export default GoogleFAQs;
